import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Check, X, Search, QrCode, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VouchersPage = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redeemId, setRedeemId] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const response = await axios.get(`${API}/vouchers`);
      setVouchers(response.data);
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      toast.error('Error al cargar vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemId.trim()) {
      toast.error('Introduce un ID de voucher');
      return;
    }
    
    setRedeeming(true);
    try {
      const response = await axios.post(`${API}/vouchers/redeem`, { voucher_id: redeemId.trim() });
      toast.success(`¡Voucher canjeado! Premio: ${response.data.reward}`);
      setRedeemDialogOpen(false);
      setRedeemId('');
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al canjear voucher');
    } finally {
      setRedeeming(false);
    }
  };

  const handleRedeemFromList = async (voucherId) => {
    if (!window.confirm('¿Canjear este voucher?')) return;
    
    try {
      const response = await axios.post(`${API}/vouchers/redeem`, { voucher_id: voucherId });
      toast.success(`¡Voucher canjeado! Premio: ${response.data.reward}`);
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al canjear');
    }
  };

  const filteredVouchers = vouchers.filter(v => 
    v.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.reward?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingVouchers = filteredVouchers.filter(v => !v.redeemed);
  const redeemedVouchers = filteredVouchers.filter(v => v.redeemed);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vouchers</h1>
          <p className="text-slate-600">Gestiona y canjea los premios de tus clientes</p>
        </div>
        <Button 
          className="btn-primary"
          onClick={() => setRedeemDialogOpen(true)}
          data-testid="redeem-voucher-btn"
        >
          <QrCode className="w-5 h-5" /> Canjear voucher
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Buscar por ID, campaña o premio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="voucher-search-input"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card-stat">
          <p className="text-2xl font-bold text-slate-900">{vouchers.length}</p>
          <p className="text-sm text-slate-500">Total emitidos</p>
        </div>
        <div className="card-stat">
          <p className="text-2xl font-bold text-amber-600">{pendingVouchers.length}</p>
          <p className="text-sm text-slate-500">Pendientes</p>
        </div>
        <div className="card-stat">
          <p className="text-2xl font-bold text-green-600">{redeemedVouchers.length}</p>
          <p className="text-sm text-slate-500">Canjeados</p>
        </div>
        <div className="card-stat">
          <p className="text-2xl font-bold text-primary">
            {vouchers.length > 0 ? Math.round((redeemedVouchers.length / vouchers.length) * 100) : 0}%
          </p>
          <p className="text-sm text-slate-500">Tasa de canje</p>
        </div>
      </div>

      {/* Vouchers List */}
      {vouchers.length === 0 ? (
        <motion.div 
          className="card-dashboard text-center py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Gift className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Sin vouchers todavía</h3>
          <p className="text-slate-600">Los vouchers se generan automáticamente cuando un cliente completa sus sellos</p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Pending Vouchers */}
          {pendingVouchers.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Pendientes de canje ({pendingVouchers.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingVouchers.map((voucher, i) => (
                  <VoucherCard 
                    key={voucher.id} 
                    voucher={voucher} 
                    onRedeem={handleRedeemFromList}
                    delay={i * 0.05}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Redeemed Vouchers */}
          {redeemedVouchers.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                Canjeados ({redeemedVouchers.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {redeemedVouchers.map((voucher, i) => (
                  <VoucherCard 
                    key={voucher.id} 
                    voucher={voucher} 
                    delay={i * 0.05}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Redeem Dialog */}
      <Dialog open={redeemDialogOpen} onOpenChange={setRedeemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Canjear Voucher</DialogTitle>
            <DialogDescription>
              Introduce el ID del voucher para canjearlo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="ID del voucher (ej: abc123-def456...)"
              value={redeemId}
              onChange={(e) => setRedeemId(e.target.value)}
              data-testid="redeem-id-input"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setRedeemDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                className="flex-1 btn-primary"
                onClick={handleRedeem}
                disabled={redeeming}
                data-testid="confirm-redeem-btn"
              >
                {redeeming ? 'Canjeando...' : 'Canjear'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const VoucherCard = ({ voucher, onRedeem, delay = 0 }) => (
  <motion.div
    className={`card-dashboard ${voucher.redeemed ? 'opacity-60' : ''}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    data-testid={`voucher-card-${voucher.id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
        voucher.redeemed ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
      }`}>
        {voucher.redeemed ? <Check className="w-5 h-5" /> : <Gift className="w-5 h-5" />}
      </div>
      <Badge variant={voucher.redeemed ? "outline" : "default"}>
        {voucher.redeemed ? 'Canjeado' : 'Pendiente'}
      </Badge>
    </div>

    <h4 className="font-bold text-slate-900 mb-1">{voucher.reward}</h4>
    <p className="text-sm text-slate-500 mb-2">{voucher.campaign_name}</p>
    
    <div className="text-xs text-slate-400 space-y-1">
      <p>ID: {voucher.id.substring(0, 20)}...</p>
      <p>Creado: {new Date(voucher.created_at).toLocaleDateString('es-ES')}</p>
      {voucher.redeemed_at && (
        <p className="text-green-600">
          Canjeado: {new Date(voucher.redeemed_at).toLocaleDateString('es-ES')}
          {voucher.redeemed_by_location_name && (
            <span className="ml-1 font-medium">
              en {voucher.redeemed_by_location_name}
            </span>
          )}
        </p>
      )}
    </div>

    {!voucher.redeemed && onRedeem && (
      <Button 
        className="w-full mt-3 btn-primary py-2"
        onClick={() => onRedeem(voucher.id)}
      >
        <Check className="w-4 h-4 mr-2" /> Canjear
      </Button>
    )}
  </motion.div>
);

export default VouchersPage;
